import globalStore from '@/services/global.state';
import theme from '@/style';
import React, {useCallback, useContext, useState} from 'react';
import {useRequest} from 'ahooks';
import {RefreshControl, ScrollView, StatusBar, View} from 'react-native';
import HomeHeader from './components/home-header';
import DownloadBanner from './components/download-ge';
import HomeBanner from './components/home-banner-v2';
import HomeGoldArea from './components/home-gold-area';
import HomeSide from './components/home-side';
import {
  getHomeGames,
  getP11MatchList,
  P11CompetitionItem,
  P11MatchItem,
} from './home.service';
import {HomeGameFloorItem, IHomeSportsCardItem} from './home.type';
import {useFocusEffect} from '@react-navigation/native';
import {FadeInView} from '@/components/basic/animations';
import {inApp, inGameBox, isPWA, useResponsiveDimensions} from '@/utils';
import HomeFAQ from './components/home-faq';
import HomePartners from './components/home-partners';
import HomePays from './components/home-pays';
import HomeSocial from './components/home-social';
import HomeLicenses from './components/home-licenses';
import {Image} from 'antd-mobile';
import DownImg from '@assets/imgs/home/down-img.webp';
import {useTranslation} from 'react-i18next';
import {downloadApk, stopBeforeDownload} from '@/utils';
import {sportsIconMap, toSportItem} from '../gr8-sports/converts';
import {getUserGroupList} from '../promotions/service';
import renderPromotionSection from './components/promotion-section';
// import renderGameSection from './components/render-game-section';
import renderSportsSection from './components/sports-section';
import renderEmptySection from './components/render-empty-section';
import RenderGameSection from './components/render-game-section';
import HomePick11 from './components/hot/pick11';
import {SportSwiperItemProps} from '../gr8-sports/components/swiper-card';
import {useChannelParams} from '@/pages/home/home.hooks';
import {sequentialStorage} from '@utils/Storage/SequentialStorage';
import {VipUpgradeContext} from '@/common-pages/vip/provider/vip-upgrade-provider';

const Home = () => {
  const {i18n} = useTranslation();
  const [show, setShow] = useState(false);
  const [homeGames, setHomeGames] = useState<HomeGameFloorItem[]>();
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(0);
  const [p11Data, setP11Data] = useState<P11CompetitionItem[] | null>(null);
  const [_p11AllLen, setP11AllLen] = useState<number>(0);
  useChannelParams();

  /** pick11 楼层数据：依赖 api/front/p11/match/list-display，不依赖 gamesList */
  React.useEffect(() => {
    const floors = homeGames ?? [];
    const hasPick11Floor = floors.some(
      f => f.uniqueKey === 'pick11' || f.tagName === 'pick11',
    );
    if (!hasPick11Floor) {
      setP11Data(null);
      setP11AllLen(0);
      return;
    }
    let cancelled = false;
    getP11MatchList()
      .then(res => {
        if (cancelled) {
          return;
        }
        console.log('getP11MatchList', res);
        // 新接口返回 {upcomingTotal, matches}
        const upcomingTotal = (res as any)?.upcomingTotal ?? 0;
        const matchesList = (res as any)?.matches ?? [];
        setP11AllLen(upcomingTotal);

        // 判断 matchesList 的数据结构
        if (matchesList.length > 0) {
          // 如果第一个元素有 matches 属性，说明是 P11CompetitionItem[]
          if ((matchesList[0] as any)?.matches) {
            setP11Data(matchesList as P11CompetitionItem[]);
          } else {
            // 否则是 P11MatchItem[]，需要包装成 P11CompetitionItem[]
            const pick11Floor = floors.find(
              f => f.uniqueKey === 'pick11' || f.tagName === 'pick11',
            );
            setP11Data([
              {
                fantasyCompetitionTitle: pick11Floor?.name,
                matches: matchesList as P11MatchItem[],
              },
            ]);
          }
        } else {
          setP11Data([]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setP11Data([]);
          setP11AllLen(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [homeGames]);

  const {data: userGroupList} = useRequest(() => getUserGroupList());

  // console.log('userGroupList', userGroupList);
  const vipUpgradeProvider = useContext(VipUpgradeContext);

  React.useEffect(() => {
    StatusBar.setBackgroundColor(theme.basicColor.primary);
    StatusBar.setBarStyle('dark-content');
    init();
  }, []);

  useFocusEffect(
    useCallback(() => {
      vipUpgradeProvider.showVipUpgrade();
    }, [vipUpgradeProvider]),
  );
  const init = (loading: boolean = true) => {
    loading && globalStore.globalLoading.next(true);
    return getHomeGames()
      .then(games => {
        setHomeGames(games);
      })
      .finally(() => globalStore.globalLoading.next(false));
  };
  const handleFocusEffect = useCallback(() => {
    const sub = globalStore.tokenSubject.subscribe(token => {
      if (token) {
        globalStore.amountCheckOut.next({});
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);
  useFocusEffect(handleFocusEffect);
  const handleRefresh = () => {
    setRefreshing(true);
    setVersion(_v => _v + 1);
    Promise.allSettled([init(false)]).finally(() => setRefreshing(false));
  };
  const {width: screenWidth} = useResponsiveDimensions();
  const onClickDownloadBtn = () => {
    if (stopBeforeDownload()) {
      return;
    }
    downloadApk();
  };

  // 4. 主组件中的渲染逻辑简化
  const renderFloorContent = (
    floor: HomeGameFloorItem,
    sports: SportSwiperItemProps[] | undefined,
    index?: number,
    noLink = false,
  ) => {
    // console.log('floor', index);
    if (floor.uniqueKey === 'Promotion') {
      if (userGroupList && userGroupList?.length > 0) {
        return (
          <div key={index}>
            {renderPromotionSection(floor, userGroupList, screenWidth)}
          </div>
        );
      }
      return null;
    }

    if (!floor.tagName?.endsWith('_sport')) {
      // return renderGameSection(floor, screenWidth);
      return (
        <div key={index}>
          <RenderGameSection floor={floor} screenWidth={screenWidth} />
        </div>
      );
    }

    if (sports && sports?.length > 0) {
      return (
        <div key={index}>{renderSportsSection(floor, sports, noLink)}</div>
      );
    }

    return <div key={index}>{renderEmptySection(floor)}</div>;
  };
  useFocusEffect(
    useCallback(() => {
      sequentialStorage.emit('updated');
    }, []),
  );
  // React.useEffect(() => {
  //   setShow(true);
  // }, []);
  return (
    <FadeInView
      style={[
        theme.fill.fill,
        {
          minWidth: screenWidth,
        },
        theme.flex.col,
      ]}>
      <HomeHeader />
      {inApp || inGameBox || isPWA() ? null : <DownloadBanner />}
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }>
        {/* 背景色设置，之前是bg-white */}
        {/* todolist: 背景色设置，之前是bg-white */}
        <div className="bg-bg">
          <View style={{height: theme.paddingSize.s}} />
          <div className="w-full pl-2">
            <HomeBanner version={version} />
          </div>
          <div className="w-full flex flex-col gap-2">
            <HomeGoldArea />
          </div>
          {homeGames?.map((floor, index) => {
            // pick11 楼层：用 getP11MatchList 数据，不依赖 gamesList
            if (floor.uniqueKey === 'pick11' || floor.tagName === 'pick11') {
              return (
                <div key={index}>
                  <HomePick11
                    data={floor}
                    p11Data={p11Data}
                    // allLen={p11AllLen}
                  />
                </div>
              );
            }
            // 优先判断渲染游戏和活动的情况
            if (!floor.tagName || !floor.tagName.endsWith('_sport')) {
              if (floor.gamesList?.length) {
                return renderFloorContent(floor, undefined, index, false);
              }
              return null;
            }
            // 确定是体育内容，判断gamesList是否存在的情况
            if (!floor.gamesList?.length) {
              return null;
            }
            try {
              const gamesList = floor.gamesList as IHomeSportsCardItem[];
              const sport = floor.tagName?.split('_')[0];
              const isSport =
                !!gamesList[0].grSportName &&
                gamesList[0].grSportName.toLowerCase() === sport.toLowerCase();
              const isHotOrPremierLeague =
                floor.uniqueKey === 'HotMatches' ||
                floor.uniqueKey === 'PremierLeague' ||
                floor.uniqueKey === 'Premier League';
              floor.isLive = isSport;
              return renderFloorContent(
                floor,
                gamesList.map(game => {
                  const id = `${game.grEventsId}-${game.grTournamentsId}-${game.grCategoriesId}-${game.grSportId}`;
                  const item = toSportItem(game);
                  return {
                    item,
                    icon:
                      floor.categoryPic ||
                      sportsIconMap[item.sport as keyof typeof sportsIconMap] ||
                      undefined,
                    isLive: item.isLive || false,
                    Sport: item.sport,
                    id: id,
                    isSport: isSport,
                  };
                }),
                index,
                !(isSport || isHotOrPremierLeague),
              );
            } catch (error) {
              // 为避免接口不匹配导致体育内容报错后全白屏，先捕获错误并不展示
              console.log('error', error);
              return null;
            }
          })}
        </div>
        {/* footer */}
        <div className="w-full bg-white">
          <HomeFAQ />
          <div className="h-[0.0625rem]" />
          <HomePartners />
          <div className="h-[0.0625rem]" />
          <HomePays />
          <div className="h-[0.0625rem]" />
          <HomeSocial />
          <div className="h-[0.0625rem]" />
          <HomeLicenses />
        </div>
        {/* <View
          style={[
            theme.fill.fillW,
            spaceAreaStyles.bottom,
            {backgroundColor: '#fff'},
          ]}
        /> */}
      </ScrollView>
      <HomeSide />
      {show && (
        <div className="fixed bottom-[5.125rem] w-[23.4375rem] m-auto flex flex-row justify-center">
          <div
            className="flex py-2 px-[0.875rem] items-center gap-2 rounded-[2.75rem] bg-[--1]"
            style={{
              boxShadow: '0px 1px 12px 0px rgba(0, 0, 0, 0.25)',
            }}>
            <div className="w-[1.75rem] h-[1.75rem]">
              <Image src={DownImg} className="w-[1.75rem] h-[1.75rem]" />
            </div>
            <span
              className="text-white text-sm font-bold leading-[66.857%]"
              onClick={() => {
                onClickDownloadBtn();
                setShow(false);
              }}
              style={{
                textShadow: '0px 0.78px 0.39px rgba(71, 129, 255, 0.50)',
              }}>
              {i18n.t('home.label.downText')}
            </span>
            <div className="w-[0.0625rem] h-6 bg-white opacity-50" />
            <div onClick={() => setShow(false)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="18"
                viewBox="0 0 19 18"
                fill="none">
                <rect
                  x="6.39258"
                  y="15.3203"
                  width="3"
                  height="13.3333"
                  rx="1.5"
                  transform="rotate(-135 6.39258 15.3203)"
                  fill="white"
                />
                <rect
                  x="15.584"
                  y="13.1992"
                  width="3"
                  height="13.3333"
                  rx="1.5"
                  transform="rotate(135 15.584 13.1992)"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      )}
    </FadeInView>
  );
};
export default Home;
